'use client';
import { Button, Drawer, Form, FormItem, Input, Modal, Select, useToast } from '@ethsign/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ColumnDef } from '@tanstack/react-table';
import React, { forwardRef, ReactNode, useImperativeHandle, useRef, useState } from 'react';
import { useForm, UseFormReturn, useWatch } from 'react-hook-form';
import * as z from 'zod';

import { AddIcon, Card, ColorRemoveIcon, ColorSearchIcon, EnsInput, Loading, Table } from '@/components';
import { AddressInfo } from '@/components/AddressInfo';
import { Tag } from '@/components/Tag';
import { NETWORK_TYPE_OPTIONS } from '@/constants';
import { createContact, deleteContactById, fetchContacts, updateContactById } from '@/services';
import { IContact } from '@/types';
import { transformAddress, WalletType, walletValidators } from '@/utils';
const formSchema = z
  .object({
    name: z
      .string()
      .min(2, {
        message: 'Username must be at least 2 characters.'
      })
      .max(70),
    network: z.string().nonempty(),
    walletAddress: z.string({ required_error: 'Please enter wallet address' }).nonempty(),
    email: z
      .string()
      .max(0, {
        message: 'Invalid email address'
      })
      .or(z.string().email({ message: 'Invalid email address' }).optional()), //兼容 empty string
    telegram: z.string().optional()
  })
  .refine(
    (data) => walletValidators[data.network as WalletType]?.(data.walletAddress),
    (data) => ({
      message: `Invalid wallet address format for the network ${data.network}.`,
      path: ['walletAddress']
    })
  );

const ContactCreateOrEditForm = forwardRef<{ form: UseFormReturn<any, any, any> }, { data?: any }>(({ data }, ref) => {
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: data || {
      name: '',
      network: WalletType.EVM
    }
  });
  const network = useWatch({
    name: 'network',
    control: form.control
  });

  useImperativeHandle(ref, () => ({
    form: form
  }));

  return (
    <Form form={form} className="space-y-[24px]">
      <FormItem name={'name'} label={'Name*'}>
        <Input placeholder="Satoshi Nakamoto" />
      </FormItem>
      <FormItem name={'network'} label={'Network*'}>
        <Select placeholder="NetWork" options={NETWORK_TYPE_OPTIONS} />
      </FormItem>
      <FormItem name={'walletAddress'} label={`Wallet Address${network === WalletType.EVM ? '/ENS Name' : ''}*`}>
        <EnsInput
          network={network}
          placeholder={network === WalletType.EVM ? '0x...' : `Enter ${network} Wallet Address`}
        />
      </FormItem>
      <FormItem name={'email'} label={'Email Address'}>
        <Input placeholder="satoshin@gmx.com" />
      </FormItem>
      <FormItem name={'telegram'} label={'Telegram'}>
        <Input placeholder="TelegramHandle" prefix={'@'} />
      </FormItem>
    </Form>
  );
});

ContactCreateOrEditForm.displayName = 'ContactCreateOrEditForm';

const ContactCreateOrEditDrawer = ({
  trigger,
  data,
  onSuccess
}: {
  trigger: ReactNode;
  data?: any;
  onSuccess: () => void;
}) => {
  const ref = useRef<{ form: any }>(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const onSubmit = async (values: any) => {
    setLoading(true);
    const submitData = {
      ...values,
      walletAddress: transformAddress(values.walletAddress, values.network)
    };
    try {
      if (data) {
        await updateContactById(data.id, submitData);
      } else {
        await createContact(submitData);
      }
      onSuccess();
      setOpen(false);
    } finally {
      setLoading(false);
    }
  };
  const onConfirm = () => {
    ref.current?.form.handleSubmit(onSubmit, console.error)();
  };
  return (
    <>
      <Drawer
        open={open}
        onOpenChange={setOpen}
        title={data ? 'Edit Contact' : 'Add Contact'}
        description={data ? 'Edit contact information' : 'Required fields are marked with asterisks (*)'}
        trigger={<span>{trigger}</span>}
        onCancel={() => setOpen(false)}
        onConfirm={onConfirm}
        confirmButtonProps={{
          loading: loading
        }}
      >
        <ContactCreateOrEditForm ref={ref} data={data} />
      </Drawer>
    </>
  );
};

const DeleteContactModal = ({ onSuccess, data }: { onSuccess: () => void; data: IContact }) => {
  const { toast } = useToast();
  const { mutateAsync, isPending } = useMutation({
    mutationFn: deleteContactById,
    mutationKey: ['deleteContactById'],
    onSuccess: () => {
      onSuccess();
      toast({
        title: 'Success',
        description: `Contact ${data.name} deleted`,
        variant: 'success'
      });
    }
  });

  return (
    <>
      <Modal
        trigger={<div className="text-gray-600 cursor-pointer">Delete</div>}
        confirmText={'Delete'}
        onConfirm={() => mutateAsync(data.id!)}
        confirmButtonProps={{
          loading: isPending
        }}
      >
        <div className="flex items-center flex-col justify-center">
          <ColorRemoveIcon />
          <div className="text-lg font-medium text-gray-900 mt-[20px]">Delete Contact</div>
          <div className="text-sm text-gray-500 mt-[8px] text-center">
            Are you sure you want to delete {data.name} from the contact list? This action can not be undone.
          </div>
        </div>
      </Modal>
    </>
  );
};

export default function Contacts() {
  const [page, setPage] = useState({
    page: 1,
    size: 10
  });
  const { data, refetch, isLoading } = useQuery({
    queryKey: ['contacts', page],
    queryFn: () => fetchContacts(page),
    select: (res) => res
  });

  const columns: ColumnDef<IContact>[] = [
    {
      header: 'Name',
      accessorKey: 'name'
    },
    {
      header: 'Email Address',
      accessorKey: 'email'
    },
    {
      header: 'Telegram',
      accessorKey: 'telegram'
    },
    {
      header: 'Wallet Address',
      accessorKey: 'walletAddress',
      cell: ({ row }) => {
        const record = row.original;
        return <AddressInfo address={record.walletAddress} network={record.network as WalletType} />;
      }
    },
    {
      header: 'Action',
      cell: ({ row }) => {
        const record = row.original;
        return (
          <div className="flex gap-[12px] items-center text-sm font-medium">
            <DeleteContactModal onSuccess={refetch} data={record} />
            <ContactCreateOrEditDrawer
              data={record}
              trigger={<div className="text-primary-700 cursor-pointer">Edit</div>}
              onSuccess={refetch}
            />
          </div>
        );
      }
    }
  ];

  return (
    <div className="p-[20px]">
      <Card
        title={
          <div className="flex items-center gap-[8px]">
            Saved Contacts <Tag>{data?.total}</Tag>
          </div>
        }
        subTitle="Keep track of contact information"
        extra={
          <ContactCreateOrEditDrawer
            onSuccess={refetch}
            trigger={
              <Button>
                <AddIcon stroke={'#FFF'} />
                Add Contact
              </Button>
            }
          />
        }
      >
        <div className="relative min-h-[150px]">
          {isLoading && <Loading></Loading>}
          {data?.rows?.length ? (
            <Table
              columns={columns}
              data={data.rows}
              pagination={{
                pageSize: page.size,
                pageIndex: page.page - 1,
                total: data?.total || 0,
                onChange: (pageNumber) => setPage({ page: pageNumber, size: page.size }),
                manualPagination: true
              }}
            />
          ) : (
            !isLoading && (
              <div className="flex flex-col items-center py-[40px]">
                <ColorSearchIcon />
                <div className="text-gray-900 font-medium text-md mt-[16px]">There&apos;s nothing here 😴</div>
                <div className="text-gray-500 text-sm mt-[4px] text-center">Add your first contact now!</div>
              </div>
            )
          )}
        </div>
      </Card>
    </div>
  );
}
